//CONFIRMAÇÃO DE DOCUMENTAÇÃO PREENCHIDA
import React, { useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import PageLoading from "components/PageLoading";
import SaveSubmitButtons from "../SaveSubmitButtons/SaveSubmitButtons";
import "./ConfirmacaoDocumentacao.css";
import {
  getRespostaAtivaByLoggedUser,
  getSindicatoByLoggedUser,
  getDocumentosByRespostaId,
  enviarSolicitacao,
} from "../../services";
import { useNavigate } from "react-router-dom";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import jsPDF from "jspdf";
import { enviarRespostaFinal } from "../../services/respostaService";
import NotificationToast from "../../components/NotificationToast/NotificationToast";
import { RespostaUsuario, ArquivoEnviado, DocumentosComErro } from "../../types";

interface DocumentGroupProps {
  titulo: string;
  subtitulo?: string;
  conteudo: (string | React.ReactElement)[];
  indicesComErro?: number[];
}

const DocumentGroup = ({
  titulo,
  subtitulo,
  conteudo,
  indicesComErro = [],
}: DocumentGroupProps) => {
  const elementos: React.ReactNode[] = [];
  for (let index = 0; index < conteudo.length; index++) {
    const itemRaw = conteudo[index];

    const isErro =
      (Array.isArray(indicesComErro) ? indicesComErro : []).includes(index) ||
      itemRaw === "Nenhum documento anexado para este campo." ||
      itemRaw === "Não preenchido" ||
      itemRaw === "Critério incompleto.";
    const proximaRaw = conteudo[index + 1];

    const item = typeof itemRaw === "string" ? itemRaw.trim() : null;
    const proxima = typeof proximaRaw === "string" ? proximaRaw.trim() : null;

    if (React.isValidElement(itemRaw)) {
      elementos.push(itemRaw);
      continue;
    }

    const className = isErro ? "highlight-error" : "";

    if (item === null || item === "") {
      elementos.push(
        <p key={index} className={className}>
          <em>Não está preenchido</em>
        </p>
      );
      continue;
    }

    const isPergunta = item.endsWith("?*") || item.endsWith("?");
    const isRespostaSimples =
      proxima !== null &&
      (proxima === "SIM" || proxima === "NÃO" || /^SIM\b|^NÃO\b/.test(proxima));

    if (isPergunta && isRespostaSimples && proxima) {
      const jaContemResposta = item
        .toLowerCase()
        .includes(proxima.toLowerCase());

      elementos.push(
        <p key={index} className={className}>
          <strong>{item}</strong>
          {!jaContemResposta && ` ${proxima}`}
        </p>
      );
      index++;
      continue;
    }

    if (item.startsWith("Observação:")) {
      elementos.push(
        <p key={index} className={className}>
          <strong>Observação: </strong>
          {item.replace(/^Observação:{1,2}/, "").trim()}
        </p>
      );
      continue;
    }

    if (item.startsWith("Quantas empresas associadas tem seu Sindicato?*")) {
      elementos.push(
        <p key={index} className={className}>
          <strong>Quantas empresas associadas tem seu Sindicato?</strong>
        </p>
      );
      continue;
    }

    if (item.startsWith("Associado")) {
      elementos.push(
        <p key={index} className={className}>
          <br></br>
          <strong></strong>
        </p>
      );
    }

    if (
      item.startsWith("Tipo do documento*") ||
      item.startsWith("Qual tipo de Ação Internacional?")
    ) {
      elementos.push(
        <p key={index} className={className}>
          <strong>{item}</strong>
        </p>
      );
      continue;
    }

    if (item.startsWith("Receita Operacional:")) {
      elementos.push(
        <p key={index} className={className}>
          <strong>Receita Operacional:</strong>{" "}
          {item.replace("Receita Operacional:", "").trim()}
        </p>
      );
      continue;
    }

    if (item.startsWith("Qual o tipo de Ação Internacional?:")) {
      const [pergunta, resposta] = item.split(":");
      elementos.push(
        <p key={index} className={className}>
          <strong>{pergunta.trim().replace(/\?+$/, "")}?</strong>{" "}
          <span className="text-muted">{resposta.trim()}</span>
        </p>
      );
      continue;
    }

    const match = item.match(/^(.+?):\s*(.+)$/);
    if (match) {
      elementos.push(
        <p key={index} className={className}>
          <strong>{match[1]}:</strong> {match[2]}
        </p>
      );
      continue;
    }

    elementos.push(
      <p key={index} className={className}>
        {index === 0 ? <strong>{itemRaw}</strong> : itemRaw}
      </p>
    );
  }

  return (
    <div className="document-group">
      <h3>{titulo}</h3>
      <h3>{subtitulo}</h3>
      {elementos}
    </div>
  );
};

interface SectionProps {
  titulo: string;
  documentos: { titulo: string; subtitulo?: string; conteudo: (string | React.ReactElement)[] }[];
  erros?: Record<string, string[]>;
}

const Section = ({ titulo, documentos, erros }: SectionProps) => {
  const listaDocumentos = Array.isArray(documentos) ? documentos : [];
  return (
    <section className="section">
      <h2 className="section-title-confirmacao">{titulo}</h2>
      {listaDocumentos.map((doc, idx) => (
        <DocumentGroup
          key={idx}
          titulo={doc.titulo}
          subtitulo={doc.subtitulo}
          conteudo={doc.conteudo}
          indicesComErro={[]}
        />
      ))}
    </section>
  );
};

interface ConfirmacaoDocumentacaoProps {
  onCancel?: () => void;
  onSubmit?: () => void | Promise<void>;
  documentos?: unknown;
}

const ConfirmacaoDocumentacao = ({ onCancel, onSubmit, documentos: _documentos }: ConfirmacaoDocumentacaoProps = {}) => {
  const [sindicatoInfo, setSindicatoInfo] = useState({
    cnpj: "Não informado",
    nome: "Não informado",
  });

  const [responsavelInfo, setResponsavelInfo] = useState<{
    id: number | null;
    responsavelPreenchimento: string;
    cargoResponsavel: string;
    percentualConclusao: number;
  }>({
    id: null,
    responsavelPreenchimento: "Não informado",
    cargoResponsavel: "Não informado",
    percentualConclusao: 0,
  });
  const [podeEditar, setPodeEditar] = useState(false);
  const [dataFim, setDataFim] = useState<unknown>(false);
  const [receitaOperacional, setReceitaOperacional] = useState(0);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");
  const [showToast, setShowToast] = useState(false);
  const [documentosComErro, setDocumentosComErro] = useState<DocumentosComErro>({
    obrigatorios: {},
    bonus: {},
    bonusExtra: {},
  });

  const [respostaUsuario, setRespostaUsuario] = useState<RespostaUsuario | null>(null);
  const navigate = useNavigate();
  const [showEnvioModal, setShowEnvioModal] = useState(false);
  const [jsonOriginal, setJsonOriginal] = useState("");
  const [arquivosEnviados, setArquivosEnviados] = useState<ArquivoEnviado[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const sindicatoResponse = await getSindicatoByLoggedUser();
        const respostaResponse = await getRespostaAtivaByLoggedUser();
        const idResposta = respostaResponse.data.id;
        const dadosGerais = respostaResponse.data || {};
        const dadosParseados = JSON.parse(respostaResponse.data.json);
        const dadosJsonOriginal = respostaResponse.data.json;
        const podeEditar = respostaResponse.data.prazoEdicao;
        const datafim = respostaResponse.data.dataFim;

        setJsonOriginal(dadosJsonOriginal);
        setPodeEditar(podeEditar);
        setDataFim(datafim);
        setRespostaUsuario(dadosParseados);
        setSindicatoInfo({
          cnpj: sindicatoResponse.data.cnpj || "Não informado",
          nome: sindicatoResponse.data.nome || "Não informado",
        });

        setResponsavelInfo({
          id: dadosGerais.id || null,
          responsavelPreenchimento:
            dadosGerais.responsavelPreenchimento || "Não informado",
          cargoResponsavel: dadosGerais.cargoResponsavel || "Não informado",
          percentualConclusao: dadosGerais.percentualConclusao || 0,
        });

        const fetchArquivos = async () => {
          try {
            const response = await getDocumentosByRespostaId(idResposta);

            setArquivosEnviados(response || []);

            localStorage.setItem(
              "arquivosEnviados",
              JSON.stringify(response || [])
            );
          } catch (error: unknown) {
            console.error("Erro ao buscar arquivos:", error);
            setArquivosEnviados([]);
          }
        };

        fetchArquivos();
      } catch (error: unknown) {
        console.error("Erro ao buscar dados:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (respostaUsuario && respostaUsuario.docsObrigatorios) {
      const resultado = gerarMapasDeDocumentos(
        respostaUsuario.docsObrigatorios,
        respostaUsuario.uploadedFilesObrigatorios,
        respostaUsuario.selectedOptionsObrigatorios,
        "obrigatorios"
      );
      setReceitaOperacional(resultado.receitaOperacional);
    }
  }, [respostaUsuario]);

  if (!respostaUsuario) return <PageLoading minHeight="80vh" />;

  const gerarMapasDeDocumentos = (
    docsEstrutura,
    arquivosEnviados = JSON.parse(
      localStorage.getItem("arquivosEnviados") || "[]"
    ),
    selectedOptions = {},
    secao = ""
  ) => {
    const encontrarArquivoMaisRecente = (nomeEsperado, arquivos) => {
      const nomeBaseEsperado = nomeEsperado
        .toLowerCase()
        .replace(/-v\d+/i, "")
        .replace(/\.(pdf|xlsx)/i, "")
        .trim();

      const relacionados = arquivos
        .filter((arquivo) => {
          const nomeReal = (arquivo.nome || arquivo.nomeArquivo || "")
            .toLowerCase()
            .trim();
          const corresponde = nomeReal.includes(nomeBaseEsperado);
          return corresponde;
        })
        .sort(
          (a, b) =>
            new Date(b.dataUpload || 0).getTime() -
            new Date(a.dataUpload || 0).getTime()
        );
      return relacionados[0];
    };

    const documentos: { titulo: string; conteudo: (string | React.ReactElement)[] }[] = [];
    let receitaOperacionalLocal = 0;
    const temasProcessados = new Set<string>();
    let qtdAssociadosInformada = 0;
    const associadosIncompletos: string[] = [];
    let camposTopico5: { quantasEmpresas: string | null; tipoDocumento: string | null } = {
      quantasEmpresas: null,
      tipoDocumento: null,
    };

    docsEstrutura.forEach((secaoData) => {
      secaoData.tema?.forEach((tema, index) => {
        const titulosIgnorados = [
          "6. RECEITA OPERACIONAL ANUAL / ARRECADAÇÃO MÍNIMA MENSAL",
          "7. EVOLUÇÃO RECEITA OPERACIONAL",
          "8. 80% DE RETENÇÃO DE ASSOCIADOS",
          "9. EVOLUÇÃO DO ASSOCIATIVISMO",
        ];
        if (titulosIgnorados.includes(tema.titulo)) return;

        temasProcessados.add(tema.titulo);
        const titulo = tema.titulo || `Documento ${index + 1}`;
        const selected = selectedOptions?.[tema.titulo];
        const respostaSelecionada =
          typeof selected === "string" ? selected : selected?.resposta || "";
        const observacao =
          typeof selected === "object" ? selected?.observacao || "" : "";
        const conteudo: (string | React.ReactElement)[] = [];

        tema.criterio?.forEach((criterio) => {
          if (criterio.titulo?.includes("Quantas empresas associadas")) {
            const campoRadio = criterio.campo?.find(
              (c) => c.tipoCampo === "radio"
            );
            camposTopico5.quantasEmpresas = campoRadio?.resposta || null;
            qtdAssociadosInformada = parseInt(campoRadio?.resposta) || 0;

            conteudo.push(`${criterio.titulo}`);
            conteudo.push(
              `Resposta: ${campoRadio?.resposta || "Não informado"}`
            );
            return;
          }
          if (criterio.titulo?.startsWith("Tipo do documento")) {
            const campoRadio = criterio.campo?.find(
              (c) => c.tipoCampo === "radio"
            );
            camposTopico5.tipoDocumento = campoRadio?.resposta || null;

            conteudo.push(`${criterio.titulo}`);
            conteudo.push(
              `Resposta: ${campoRadio?.resposta || "Não informado"}`
            );
            return;
          }

          if (/Associado \d+/.test(criterio.titulo)) {
            const numeroAssociado = parseInt(criterio.titulo.match(/\d+/)?.[0]);
            if (numeroAssociado > qtdAssociadosInformada) return;

            conteudo.push(`${criterio.titulo}`);

            let cnpjPreenchido = false;
            let razaoPreenchida = false;
            let pdfAnexado = false;

            criterio.campo?.forEach((campo) => {
              const label = campo.titulo || "Campo";
              const resposta = campo.resposta || "Não preenchido";

              if (
                respostaSelecionada.toUpperCase() === "NÃO" &&
                (campo.tipoCampo === "pdf" || campo.tipoCampo === "xlsx")
              ) {
                return;
              }

              if (
                label.toLowerCase().includes("cnpj") &&
                campo.resposta?.trim()
              ) {
                cnpjPreenchido = true;
              }

              if (
                label.toLowerCase().includes("razão social") &&
                campo.resposta?.trim()
              ) {
                razaoPreenchida = true;
              }

              if (campo.tipoCampo === "pdf" && campo.nomeArquivo) {
                const nomeEsperado = campo.nomeArquivo.toLowerCase().trim();
                const arquivoEncontrado = encontrarArquivoMaisRecente(
                  nomeEsperado,
                  arquivosEnviados
                );
                if (arquivoEncontrado) {
                  pdfAnexado = true;
                  conteudo.push("Arquivo: " + arquivoEncontrado.nomeArquivo);
                } else {
                  conteudo.push(
                    "Arquivo: Nenhum documento anexado para este campo."
                  );
                }
              } else {
                if (label !== criterio.titulo) {
                  conteudo.push(`${label}: ${resposta}`);
                } else {
                  conteudo.push(`${resposta}`);
                }
              }
            });

            if (!cnpjPreenchido || !razaoPreenchida || !pdfAnexado) {
              associadosIncompletos.push(`Associado ${numeroAssociado}`);
            }

            return;
          }

          if (criterio?.titulo) {
            conteudo.push(`${criterio.titulo}`);
          }

          criterio.campo?.forEach((campo) => {
            const label = campo.titulo || "Resposta";
            let resposta = campo.resposta || "Não preenchido";
            let respostaNivel1 = "";
            let respostaNivel2 = "";
            let respostaNivel3 = "";

            if (
              tema.titulo === "1. AÇÃO INTERNACIONAL" &&
              campo.idCampo === "tipo-acao-internacional" &&
              campo.resposta !== ""
            ) {
              respostaNivel1 = campo.resposta;

              if (
                respostaNivel1 !== "" &&
                respostaNivel1 !== "Certificado de origem"
              ) {
                campo.alternativa?.forEach((alt2) => {
                  if (alt2.titulo === respostaNivel1) {
                    if (alt2.campo[0].resposta !== "") {
                      respostaNivel2 = alt2.campo[0].resposta;

                      if (
                        respostaNivel2 !== "Capacitação" &&
                        respostaNivel2 !==
                        "Estudos de inteligência em Negócios Internacionais"
                      ) {
                        alt2.campo[0].alternativa.forEach((alt3) => {
                          if (alt3.valor === respostaNivel2) {
                            respostaNivel3 = alt3.campo[0].resposta;

                            const campoPdf = alt3.campo.find(
                              (c) => c.tipoCampo === "pdf"
                            );

                            if (campoPdf && campoPdf.nomeArquivo) {
                              const nomeEsperadoArquivo = campoPdf.nomeArquivo
                                .toLowerCase()
                                .trim();

                              const arquivo = encontrarArquivoMaisRecente(
                                nomeEsperadoArquivo,
                                arquivosEnviados
                              );

                              if (arquivo) {
                                conteudo.push(
                                  "Arquivo: " + arquivo.nomeArquivo
                                );
                              } else {
                                conteudo.push(
                                  "Nenhum documento anexado para este campo."
                                );
                              }
                            }
                          }
                        });
                      }
                    }
                  }
                });
              } else {
                resposta = respostaNivel1;

                const alternativaSelecionada = campo.alternativa?.find(
                  (alt) => alt.titulo === respostaNivel1
                );

                if (alternativaSelecionada?.campo) {
                  const campoPdf = alternativaSelecionada.campo.find(
                    (c) => c.tipoCampo === "pdf"
                  );

                  if (campoPdf && campoPdf.nomeArquivo) {
                    const nomeEsperadoArquivo = campoPdf.nomeArquivo
                      .toLowerCase()
                      .trim();

                    const arquivo = encontrarArquivoMaisRecente(
                      nomeEsperadoArquivo,
                      arquivosEnviados
                    );

                    if (arquivo) {
                      conteudo.push("Arquivo: " + arquivo.nomeArquivo);
                    } else {
                      conteudo.push(
                        "Nenhum documento anexado para este campo."
                      );
                    }
                  }
                }
              }

              if (
                respostaNivel1 !== "Certificado de origem" &&
                respostaNivel2 === ""
              ) {
                conteudo.push("Critério incompleto.");
              } else if (
                respostaNivel2 ===
                "Participação em Missões Internacionais organizadas pelo CIN" &&
                respostaNivel3 === ""
              ) {
                conteudo.push("Critério incompleto.");
              }
            }

            if (respostaNivel2 !== "") {
              resposta = resposta + " → " + respostaNivel2;
            }
            if (respostaNivel3 !== "") {
              resposta = resposta + " → " + respostaNivel3;
            }

            if (campo.tipoCampo === "pdf" || campo.tipoCampo === "xlsx") {
              const nomeEsperado = campo.nomeArquivo.toLowerCase().trim();
              const arquivoEncontrado = encontrarArquivoMaisRecente(
                nomeEsperado,
                arquivosEnviados
              );

              if (arquivoEncontrado) {
                conteudo.push("Arquivo: " + arquivoEncontrado.nomeArquivo);
              } else if (
                !arquivoEncontrado &&
                criterio.campo[0].resposta === "NÃO"
              ) {
              } else {
                conteudo.push("Nenhum documento anexado para este campo.");
              }
            } else {
              conteudo.push(`${label}: ${resposta}`);
            }

            if (campo.titulo === "Receita Operacional*") {
              receitaOperacionalLocal = parseFloat(campo.resposta) || 0;
            }
          });
        });

        if (respostaSelecionada)
          conteudo.push(`Resposta: ${respostaSelecionada}`);
        if (observacao) conteudo.push(` ${observacao}`);

        documentos.push({
          titulo,
          conteudo,
        });
      });
    });

    const resultado: Record<string, unknown> = {
      [secao]: documentos,
      receitaOperacional: receitaOperacionalLocal,
      associadosIncompletos,
    };
    if (secao === "obrigatorios") {
      (resultado as { camposTopico5?: typeof camposTopico5 }).camposTopico5 = camposTopico5;
    }
    return resultado as {
      obrigatorios?: { titulo: string; conteudo: (string | React.ReactElement)[] }[];
      bonus?: { titulo: string; conteudo: (string | React.ReactElement)[] }[];
      bonusExtra?: { titulo: string; conteudo: (string | React.ReactElement)[] }[];
      receitaOperacional: number;
      associadosIncompletos: string[];
      camposTopico5?: { quantasEmpresas: string | null; tipoDocumento: string | null };
    };
  };

  const documentosObrigatorios = gerarMapasDeDocumentos(
    respostaUsuario.docsObrigatorios,
    respostaUsuario.selectedOptionsObrigatorios,
    respostaUsuario.uploadedFilesObrigatorios,
    "obrigatorios"
  );

  const documentosBonus = gerarMapasDeDocumentos(
    respostaUsuario.docsBonus,
    respostaUsuario.selectedOptionsBonus,
    respostaUsuario.uploadedFilesBonus,
    "bonus"
  );

  const documentosBonusExtra = gerarMapasDeDocumentos(
    respostaUsuario.docsBonusExtra,
    respostaUsuario.selectedOptionsBonusExtra,
    respostaUsuario.uploadedFilesBonusExtra,
    "bonusExtra"
  );


  const validarDocumentos = (
    documentos: { titulo: string; conteudo: (string | React.ReactElement)[] }[]
  ): Record<string, string[]> => {
    const erros: Record<string, string[]> = {};

    documentos.forEach((doc) => {
      const titulo = doc.titulo;
      const linhas = doc.conteudo.filter((x): x is string => typeof x === "string");

      const errosDoc: string[] = [];

      const respostaLine = linhas.find((linha) =>
        linha.startsWith("Resposta:")
      );
      const resposta = respostaLine?.split("Resposta:")[1]?.trim();
      const respostaInvalida =
        !resposta || resposta.toLowerCase().includes("não preenchido");

      const linhasDeArquivo = linhas.filter(
        (linha) =>
          linha.startsWith("Arquivo:") || linha.startsWith("Nenhum documento")
      );

      const temArquivoValido = linhasDeArquivo.some(
        (linha) =>
          linha.startsWith("Arquivo:") &&
          !linha.toLowerCase().includes("nenhum documento")
      );

      const possuiResposta = !!respostaLine;
      const possuiArquivo = linhasDeArquivo.length > 0;

      if (possuiResposta && !possuiArquivo) {
        if (respostaInvalida) {
          errosDoc.push("Resposta não informada.");
        }
      } else if (possuiResposta && possuiArquivo) {
        if (respostaInvalida) {
          errosDoc.push("Resposta não informada.");
        } else if (resposta.toUpperCase() === "SIM" && !temArquivoValido) {
          errosDoc.push("Documento obrigatório não anexado.");
        }
      } else if (!possuiResposta && possuiArquivo) {
        if (!temArquivoValido) {
          errosDoc.push("Documento obrigatório não anexado.");
        }
      } else if (!possuiResposta && !possuiArquivo) {
        errosDoc.push(
          "Documento incompleto. Nenhuma resposta ou arquivo foi informado."
        );
      }

      let associadoAtual: string | null = null;
      const errosAssociados: Record<string, string[]> = {};

      linhas.forEach((linha) => {
        if (linha.startsWith("Associado")) {
          associadoAtual = linha.trim();
          errosAssociados[associadoAtual] = [];
        }

        if (!associadoAtual) return;

        if (linha.startsWith("CNPJ:")) {
          const cnpj = linha.split("CNPJ:")[1]?.trim();
          if (!cnpj || cnpj.toLowerCase().includes("não preenchido")) {
            errosAssociados[associadoAtual].push("CNPJ não preenchido.");
          }
        }

        if (linha.startsWith("Razão Social:")) {
          const razao = linha.split("Razão Social:")[1]?.trim();
          if (!razao || razao.toLowerCase().includes("não preenchido")) {
            errosAssociados[associadoAtual].push(
              "Razão Social não preenchida."
            );
          }
        }

        if (linha.startsWith("Arquivo:")) {
          const arquivo = linha.split("Arquivo:")[1]?.trim();
          if (!arquivo || arquivo.toLowerCase().includes("nenhum documento")) {
            errosAssociados[associadoAtual].push("Arquivo não anexado.");
          }
        }
      });

      Object.entries(errosAssociados).forEach(([associado, errosAssoc]: [string, string[]]) => {
        if (errosAssoc.length > 0) {
          errosDoc.push(`${associado}: ${errosAssoc.join(" ")}`);
        }
      });

      if (errosDoc.length > 0) {
        if (!erros[titulo]) {
          erros[titulo] = [];
        }
        erros[titulo].push(...errosDoc);
      }
    });

    return erros;
  };

  const handleSubmit = () => {
    if (dataFim && !podeEditar) {
      setShowToast(true);
      setToastMessage("Prazo encerrado, você não pode mais enviar.");
      setToastType("error");
      return;
    }

    const camposT5 = documentosObrigatorios?.camposTopico5 ?? {
      quantasEmpresas: null,
      tipoDocumento: null,
    };
    const errosT5: string[] = [];

    if (
      !camposT5?.quantasEmpresas ||
      isNaN(parseInt(camposT5.quantasEmpresas))
    ) {
      errosT5.push("o campo 'Quantas empresas associadas tem seu Sindicato?'");
    }

    const tipoDoc = camposT5?.tipoDocumento?.toLowerCase()?.trim() || "";

    if (
      !tipoDoc ||
      !(
        tipoDoc.includes("relatório de títulos") ||
        tipoDoc.includes("liquidação de títulos")
      )
    ) {
      errosT5.push("o campo 'Tipo do documento*'");
    }

    if (errosT5.length > 0) {
      setShowToast(true);
      setToastMessage(
        `Preencha corretamente ${errosT5.join(" e ")} no Tópico 5.`
      );
      setToastType("error");
      return;
    }

    if (
      documentosObrigatorios.associadosIncompletos &&
      documentosObrigatorios.associadosIncompletos.length > 0
    ) {
      setShowToast(true);
      setToastMessage(
        `Preencha todos os campos obrigatórios para os seguintes associados: ${documentosObrigatorios.associadosIncompletos.join(
          ", "
        )}`
      );
      setToastType("error");
      return;
    }

    const errosObrig = validarDocumentos(documentosObrigatorios.obrigatorios ?? []);
    const errosB = validarDocumentos(documentosBonus.bonus ?? []);
    const errosBExtra = validarDocumentos(documentosBonusExtra.bonusExtra ?? []);

    setDocumentosComErro({
      obrigatorios: errosObrig,
      bonus: errosB,
      bonusExtra: errosBExtra,
    });

    const temErrosObrig = Object.keys(errosObrig).length > 0;
    const temErrosB = Object.keys(errosB).length > 0;
    const temErrosBExtra = Object.keys(errosBExtra).length > 0;

    if (!temErrosObrig && !temErrosB && !temErrosBExtra) {
      setShowToast(true);
      setToastMessage("Envio feito com sucesso!");
      setToastType("success");
      handleConfirmEnvio();
    } else {
      setShowToast(true);
      setToastMessage("Existem erros a serem corrigidos antes do envio.");
      setToastType("error");
    }
  };

  const handleConfirmEnvio = async () => {
    setShowEnvioModal(false);


    try {
      const id = responsavelInfo.id;
      const solicitacaoId = Number(id);

      const gerarTextoResumo = () => {
        const lines: string[] = [];
        lines.push(`CNPJ: ${sindicatoInfo.cnpj}`);
        lines.push(`Sindicato: ${sindicatoInfo.nome}`);
        lines.push(`Receita Operacional: ${receitaOperacional}`);
        lines.push("");

        const adicionarSecao = (
          titulo: string,
          docs: { titulo: string; conteudo: (string | React.ReactElement)[] }[]
        ) => {
          lines.push(titulo);
          lines.push("=".repeat(titulo.length));
          docs?.forEach((d) => {
            lines.push(String(d.titulo || ""));
            (d.conteudo || []).forEach((c) => {
              if (typeof c === "string") lines.push(c);
            });
            lines.push("");
          });
        };

        adicionarSecao(
          "DOCUMENTOS OBRIGATÓRIOS",
          (documentosObrigatorios.obrigatorios ?? []) as any
        );
        adicionarSecao(
          "DOCUMENTOS BÔNUS",
          (documentosBonus.bonus ?? []) as any
        );
        adicionarSecao(
          "DOCUMENTOS BÔNUS EXTRA",
          (documentosBonusExtra.bonusExtra ?? []) as any
        );

        lines.push("JSON original (referência):");
        lines.push(jsonOriginal);

        return lines.join("\n");
      };

      const gerarPdfResumo = (): Blob => {
        const texto = gerarTextoResumo();

        const doc = new jsPDF({
          unit: "pt",
          format: "a4",
          orientation: "portrait",
        });

        const marginX = 40;
        const marginY = 50;
        const maxWidth = 520;
        const fontSize = 10;
        doc.setFontSize(fontSize);

        const lines: string[] = (doc as any).splitTextToSize
          ? (doc as any).splitTextToSize(texto, maxWidth)
          : [texto];

        let y = marginY;
        (lines || []).forEach((ln: string) => {
          if (y > 770) return;
          doc.text(ln, marginX, y, { maxWidth });
          y += 14;
        });

        const out: any = (doc as any).output?.("arraybuffer");
        // jsPDF mock no Jest faz output() retornar string; tratamos isso.
        if (out instanceof ArrayBuffer) {
          return new Blob([out], { type: "application/pdf" });
        }
        const bytes = new TextEncoder().encode(String(out ?? ""));
        return new Blob([bytes], { type: "application/pdf" });
      };

      const pdfResumo = gerarPdfResumo();

      // Endpoint novo (V2): POST /api/avaliacoes/solicitacoes/enviar
      try {
        if (!Number.isFinite(solicitacaoId) || solicitacaoId <= 0) {
          throw new Error("solicitacaoId inválido para endpoint V2");
        }

        await enviarSolicitacao({
          solicitacaoId,
          PdfResumo: pdfResumo,
        });
      } catch (e) {
        // Fallback legado (se o endpoint novo não servir para este fluxo)
        const dadosEnvio = {
          json: JSON.parse(jsonOriginal),
          responsavelPreenchimento: responsavelInfo.responsavelPreenchimento,
          cargoResponsavel: responsavelInfo.cargoResponsavel,
          receitaOperacional,
          percentualConclusao: responsavelInfo.percentualConclusao,
          arquivos: arquivosEnviados as never[],
        };

        await enviarRespostaFinal(id as number, dadosEnvio);
      }

      setTimeout(() => {
        navigate(-1);
      }, 2000);
    } catch (error: unknown) {
      const err = error as { response?: { data?: unknown }; message?: string };
      console.error(
        "❌ Erro ao enviar resposta final:",
        err?.response?.data || err?.message
      );
    }
  };

  const handleCancel = () => navigate(-1);

  return (
    <Container className="confirmacao-container">
      <h1 className="page-title">CONFIRMAÇÃO DA DOCUMENTAÇÃO INFORMADA</h1>

      <section className="section">
        <h2 className="section-title-confirmacao">DADOS DO SINDICATO</h2>
        <div className="info-line">
          <span>
            <strong>CNPJ:</strong> {sindicatoInfo.cnpj}
          </span>
          <span>
            <strong>Nome do Sindicato:</strong> {sindicatoInfo.nome}
          </span>
        </div>
      </section>

      <Section
        titulo="DOCUMENTOS OBRIGATÓRIOS"
        documentos={documentosObrigatorios.obrigatorios ?? []}
        erros={documentosComErro.obrigatorios}
      />

      <Section
        titulo="DOCUMENTOS BÔNUS"
        documentos={documentosBonus.bonus ?? []}
        erros={documentosComErro.bonus}
      />

      <Section
        titulo="DOCUMENTOS BÔNUS EXTRA"
        documentos={documentosBonusExtra.bonusExtra ?? []}
        erros={documentosComErro.bonusExtra}
      />

      <div className="buttons-container">
        <SaveSubmitButtons
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          showCancel={true}
          podeEditar={true}
          showSave={false}
          temAdequacao={false}
          submitLabel="ENVIAR"
          onlySubmit={true}
        />
      </div>

      <ConfirmationModal
        isOpen={showEnvioModal}
        title="Confirmar Envio"
        message="Ao clicar em 'Enviar' toda a documentação informada será enviada para o Desenvolvimento Sindical fazer a análise. Deseja realmente enviar a documentação?"
        onConfirm={() => { handleSubmit(); }}
        onCancel={() => setShowEnvioModal(false)}
      />

      <NotificationToast
        show={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        type={toastType}
      />
    </Container>
  );
};

export default ConfirmacaoDocumentacao;
